import React, {useEffect, useState, useRef} from 'react'
import PageApp from '../../../Page';
import { HomeOutlined, UnorderedListOutlined, ExclamationCircleFilled, UploadOutlined, DownloadOutlined, DollarCircleOutlined, FolderOpenOutlined, ApiOutlined } from '@ant-design/icons';
import {Modal, Divider, Button, message, Space, Tooltip, Upload, Alert, Progress, Radio, InputNumber} from 'antd';
import {importBulk, importBulkConfirm} from '../../../../apis/NominaAcumuladoApi';
import './styles.scss';

const routes = [
    {
        href: '',
        title: <>
        <HomeOutlined />
        <span>Inicio</span>
        </>,
    },
    {
        href: '',
        title: <>
        <DollarCircleOutlined />
        <span>Nómina</span>
        </>,
    },
    {
        href: '',
        title: <>
        <ApiOutlined />
        <span>Interfaces</span>
        </>,
    },
    {
        href: '',
        title: <>
        <FolderOpenOutlined />
        <span>Inicial</span>
        </>,
    },
    {
        href: '',
        title: (
        <>
            <UnorderedListOutlined />
            <span>Importar Acumulados</span>
        </>
        ),
    }
];

const ImportarAcumulados = () => {
    const [uploading, setUploading] = useState(false);  
    const [progress, setProgress] = useState(0);
    const [tipo, setTipo] = useState('mensuales');
    const [anio, setAnio] = useState(null);
    const progressTimer = useRef(null);

    useEffect(() => {
      return () => {
        if (progressTimer.current) {
          clearInterval(progressTimer.current);
        }
      };
    }, []);

    const handleImport = async (file, _fileList) => {
      const forceSave = false;

      if (!anio) {
        message.warning('Especifica el año del acumulado antes de importar');
        return false;
      }

      setUploading(true);
      setProgress(0);
      if (progressTimer.current) {
        clearInterval(progressTimer.current);
      }
      // Progreso simulado para dar feedback sin cargar al backend
      progressTimer.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) return prev;
          return prev + 5;
        });
      }, 300);
      let shouldStopLoading = true;
      
      try {
        const response = await importBulk(
          file,
          tipo,
          forceSave,
          anio
        );

        if (response.ok) {
          const result = response.payload;
          
          const hasErrors = Array.isArray(result.errors) && result.errors.length > 0;

          // Si requiere confirmación (hay errores y no se guardó)
          if (result.requires_confirmation) {
            shouldStopLoading = false;
            Modal.confirm({
              title: 'Confirmar importación con errores',
              icon: <ExclamationCircleFilled />,
              content: (
                <div>
                  <p>{result.message}</p>
                  <p style={{marginTop: 16}}>Total de registros: <strong>{result.total}</strong></p>
                  <p>Registros exitosos: <strong style={{color: '#52c41a'}}>{result.successful}</strong></p>
                  <p>Registros fallidos: <strong style={{color: '#ff4d4f'}}>{result.failed}</strong></p>
                  {result.errors && result.errors.length > 0 && (
                    <>
                      <p style={{marginTop: 16, fontWeight: 'bold'}}>Errores encontrados (primeros 10):</p>
                      <ul style={{maxHeight: 200, overflow: 'auto', fontSize: '12px', background: '#fafafa', padding: 10, borderRadius: 4}}>
                        {result.errors.map((error, index) => (
                          <li key={index} style={{marginBottom: 4}}>{error}</li>
                        ))}
                      </ul>
                      {result.total_errors > 10 && (
                        <p style={{fontSize: '12px', color: '#888', marginTop: 8}}>
                          ...y {result.total_errors - 10} errores más
                        </p>
                      )}
                    </>
                  )}
                </div>
              ),
              width: 700,
              okText: 'Sí, guardar registros exitosos',
              cancelText: 'No, cancelar importación',
              onOk: async () => {
                setUploading(true);
                const confirmedResponse = await importBulkConfirm(
                  tipo,
                  result.cached_records,
                  anio
                );
                
                if (confirmedResponse.ok) {
                  const confirmedResult = confirmedResponse.payload;
                  if (confirmedResult.saved) {
                    message.success(`Se guardaron correctamente ${confirmedResult.successful} acumulados`);
                  }
                } else {
                  message.error(confirmedResponse.message || 'Error al confirmar la importación');
                }
                setUploading(false);
              },
              onCancel: () => {
                message.info('Importación cancelada');
                setUploading(false);
              }
            });
          } else {
            // No requiere confirmación (todo exitoso o ya se guardó o error de validación)
            const hasValidationIssues = hasErrors || (result.total_errors > 0) || (result.failed > 0);
            
            if (hasValidationIssues) {
              const firstError = Array.isArray(result.errors) && result.errors.length > 0 ? result.errors[0] : 'Error en la importación';
              message.error(firstError);
              Modal.warning({
                title: result.total === 0 ? 'Error en la importación' : 'Importación completada con errores',
                content: (
                  <div>
                    {result.total > 0 && (
                      <>
                        <p>Total de registros: <strong>{result.total}</strong></p>
                        <p>Exitosos: <strong style={{color: '#52c41a'}}>{result.successful}</strong></p>
                        <p>Fallidos: <strong style={{color: '#ff4d4f'}}>{result.failed}</strong></p>
                      </>
                    )}
                    {result.errors && result.errors.length > 0 && (
                      <>
                        <p style={{marginTop: result.total > 0 ? 16 : 0, fontWeight: 'bold'}}>Errores:</p>
                        <ul style={{maxHeight: 200, overflow: 'auto', fontSize: '12px', background: '#fafafa', padding: 10}}>
                          {result.errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                        {result.total_errors > 10 && (
                          <p style={{fontSize: '12px', color: '#888', marginTop: 8}}>
                            ...y {result.total_errors - 10} errores más
                          </p>
                        )}
                      </>
                    )}
                  </div>
                ),
                width: 600,
              });
            } else {
              message.success(`Se importaron correctamente ${result.successful} acumulados`);
            }
          }
        }
      } catch (error) {
        message.error('Error al importar el archivo');
      } finally {
        if (shouldStopLoading) {
          setUploading(false);
        }
        if (progressTimer.current) {
          clearInterval(progressTimer.current);
        }
        setProgress(100);
        setTimeout(() => setProgress(0), 500);
      }

      return false;
    };

    const downloadTemplate = () => {
      // Mismo formato ancho que el reporte de acumulados (aplica a mensuales y anuales)
      const csvContent = 'Número:,Nombre:,ID# de Concepto:,Concepto:,ENE,FEB,MAR,ABR,MAY,JUN,JUL,AGO,SEP,OCT,NOV,DIC,OTROS,TOTAL\n' +
                         '1,"Perez Lopez, Juan Carlos",1,Sueldo Ordinario,10000.00,10000.00,10000.00,10000.00,10000.00,10000.00,10000.00,10000.00,0,0,0,0,0,80000.00\n' +
                         '1,"Perez Lopez, Juan Carlos",101,ISR,800.00,800.00,800.00,800.00,800.00,800.00,800.00,800.00,0,0,0,0,0,6400.00\n' +
                         '2,"Ruiz Perez, Maria Elena",1,Sueldo Ordinario,9500.00,9500.00,9500.00,9500.00,9500.00,9500.00,9500.00,9500.00,0,0,0,0,0,76000.00';
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `plantilla_importar_acumulados_${tipo}.csv`;
      link.click();
    };

    return <PageApp 
      routes={routes}
      title={'Importar Acumulados'}>
        <>
          <Alert
            type="info"
            message={tipo === 'anuales'
              ? 'Formato ancho (meses en columnas): el año se indica en el campo inferior y el monto se toma de la columna TOTAL. Formato largo: el anio se lee desde el archivo.'
              : 'Formato ancho (meses en columnas): el año se indica en el campo inferior y ENE..DIC genera un registro por mes (solo meses con valor distinto de 0). Formato largo: el anio y el num_mes se leen desde el archivo.'}
            showIcon
          />

          <div style={{marginTop: 16, marginBottom: 16, padding: 16, background: '#fafafa', borderRadius: 8}}>
            <div>
              <label style={{display: 'block', marginBottom: 8, fontWeight: 500}}>Tipo de Acumulado:</label>
              <Radio.Group 
                value={tipo} 
                onChange={(e) => setTipo(e.target.value)}
                disabled={uploading}
              >
                <Radio value="mensuales">Mensuales</Radio>
                <Radio value="anuales">Anuales</Radio>
              </Radio.Group>
            </div>
            <div style={{marginTop: 16}}>
              <label style={{display: 'block', marginBottom: 8, fontWeight: 500}}>Año del acumulado:</label>
              <InputNumber
                min={2000}
                max={2100}
                placeholder="Ej. 2025"
                value={anio}
                onChange={(value) => setAnio(value)}
                disabled={uploading}
                style={{width: 160}}
              />
            </div>
          </div>

          <Space style={{marginBottom: 16}}>
            <Upload
              beforeUpload={handleImport}
              accept=".csv,.xlsx,.xls"
              showUploadList={false}
              disabled={uploading}
            >
              <Tooltip 
                title=""
                placement="top"
              >
                <Button 
                  type="primary"
                  icon={<UploadOutlined />} 
                  disabled={uploading}
                  loading={uploading}
                  size="large"
                >
                  Importar Acumulados
                </Button>
              </Tooltip>
            </Upload>
            <Button 
              icon={<DownloadOutlined />} 
              onClick={downloadTemplate}
              size="large"
            >
              Descargar Plantilla
            </Button>

          </Space>

          {uploading && (
            <div style={{maxWidth: 400, marginBottom: 16}}>
              <Progress percent={progress} status={progress >= 100 ? 'success' : 'active'} />
            </div>
          )}
          
          <Divider/>

          <div style={{background: '#fafafa', padding: 24, borderRadius: 8}}>
            <h3>Información de Importación</h3>
            {tipo === 'mensuales' ? (
              <>
                <p><strong>Formato ancho (meses en columnas):</strong> columnas <code>Número:</code> (= nomina_employee_number),
                <code> ID# de Concepto:</code> (= clave del concepto), <code>Concepto:</code>, ENE..DIC, OTROS y TOTAL.
                El año se indica en el campo "Año". Cada mes con valor distinto de 0 genera un registro de acumulado mensual
                (num_mes 1..12); las columnas TOTAL y OTROS se ignoran en mensuales.</p>
                <p><strong>Formato largo:</strong> columnas obligatorias num_empleado, clave_concepto, anio y num_mes;
                opcionales monto, exento, descuento, dias, horas (anio y num_mes se leen desde el archivo).</p>
              </>
            ) : (
              <>
                <p><strong>Formato ancho (meses en columnas):</strong> columnas <code>Número:</code> (= nomina_employee_number),
                <code> ID# de Concepto:</code> (= clave del concepto), <code>Concepto:</code>, ENE..DIC, OTROS y TOTAL.
                El año se indica en el campo "Año" y la columna TOTAL se guarda como monto (ENE..DIC y OTROS no se persisten).</p>
                <p><strong>Formato largo:</strong> columnas obligatorias num_empleado, clave_concepto y anio;
                opcionales monto, exento, descuento, dias, horas (el anio se lee desde el archivo).</p>
              </>
            )}
          </div>
        </>
    </PageApp>
}   

export default ImportarAcumulados;
